import { useState } from "react";
import AddressAutocomplete from "./AddressAutocomplete";

export default function SettingsModal({ isOpen, onClose, homeLocation, onSaveHome, onClearHome, browserLocation, onLogout, userEmail, kudosPromptEnabled, onToggleKudosPrompt, onRequestMapPick }) {
  const [address, setAddress] = useState(homeLocation?.address || "");
  const [lat, setLat] = useState(homeLocation?.lat || null);
  const [lng, setLng] = useState(homeLocation?.lng || null);
  const [saved, setSaved] = useState(false);

  if (!isOpen) return null;

  const handleSelect = ({ address: addr, lat: newLat, lng: newLng }) => {
    setAddress(addr);
    setLat(newLat);
    setLng(newLng);
    setSaved(false);
  };

  const handleSave = () => {
    if (!lat || !lng) {
      alert("Please select an address from the suggestions first.");
      return;
    }
    onSaveHome({ address, lat, lng });
    setSaved(true);
    setTimeout(() => setSaved(false), 2000);
  };

  const handleClear = () => {
    setAddress("");
    setLat(null);
    setLng(null);
    onClearHome();
    setSaved(false);
  };

  return (
    <div className="fixed inset-0 z-[2000] flex items-center justify-center p-4">
      {/* Backdrop */}
      <div className="absolute inset-0 bg-black/50 backdrop-blur-sm" onClick={onClose}></div>

      {/* Modal */}
      <div className="relative bg-white/95 backdrop-blur-xl rounded-3xl shadow-2xl border border-white/50 p-6 max-w-md w-full">
        {/* Close button */}
        <button
          onClick={onClose}
          className="absolute top-4 right-4 w-8 h-8 flex items-center justify-center rounded-full hover:bg-gray-100 transition text-gray-400"
        >
          ✕
        </button>

        {/* Header */}
        <div className="flex items-center gap-3 mb-6">
          <div className="w-10 h-10 bg-gray-100 rounded-xl flex items-center justify-center text-lg">⚙️</div>
          <div>
            <h2 className="font-condensed text-2xl font-bold text-gray-800 uppercase tracking-tight">Settings</h2>
            <p className="text-xs text-gray-400">Customize your Leafsy experience</p>
          </div>
        </div>

        {/* Divider */}
        <div className="border-t border-gray-200/60 mb-5"></div>

        {/* Location status */}
        <div className="mb-5">
          <p className="font-condensed text-sm font-semibold text-gray-600 uppercase tracking-wide mb-2">Location Status</p>
          <div className="flex flex-col gap-1.5 text-xs">
            <div className="flex items-center gap-2">
              <span className={`w-2 h-2 rounded-full ${browserLocation ? "bg-green-500" : "bg-red-400"}`}></span>
              <span className="text-gray-600">
                Browser GPS: {browserLocation ? "Active" : "Not available"}
              </span>
            </div>
            <div className="flex items-center gap-2">
              <span className={`w-2 h-2 rounded-full ${homeLocation?.lat ? "bg-green-500" : "bg-gray-300"}`}></span>
              <span className="text-gray-600">
                Home location: {homeLocation?.lat ? "Set" : "Not set"}
              </span>
            </div>
          </div>
        </div>

        {/* Divider */}
        <div className="border-t border-gray-200/60 mb-5"></div>

        {/* Home location section */}
        <div className="mb-2">
          <p className="font-condensed text-sm font-semibold text-gray-600 uppercase tracking-wide mb-1">Home Location</p>
          <p className="text-xs text-gray-400 mb-3">Set a default location so you don't need to share your GPS. This will be used to show distances and center the map.</p>

          <AddressAutocomplete
            value={address}
            onChange={setAddress}
            onSelect={handleSelect}
          />

          {onRequestMapPick && (
            <button
              type="button"
              onClick={onRequestMapPick}
              className="btn-pop mt-2 w-full flex items-center justify-center gap-2 py-2 rounded-xl text-sm font-medium text-green-700 bg-green-50 border border-green-200 hover:bg-green-100"
            >
              <span>📍</span> Pick on map
            </button>
          )}

          {lat && lng && (
            <p className="text-xs text-green-600 mt-2 flex items-center gap-1">
              <span className="w-1.5 h-1.5 bg-green-500 rounded-full"></span>
              Location selected: {lat.toFixed(4)}, {lng.toFixed(4)}
            </p>
          )}

          {homeLocation?.address && !address && (
            <p className="text-xs text-gray-400 mt-2">Current: {homeLocation.address}</p>
          )}

          <div className="flex gap-2 mt-3">
            <button
              onClick={handleSave}
              className="btn-pop flex-1 bg-green-500 text-white py-2.5 rounded-xl text-sm font-semibold hover:bg-green-600 shadow-sm"
            >
              {saved ? "✓ Saved!" : "Save Location"}
            </button>
            {homeLocation?.lat && (
              <button
                onClick={handleClear}
                className="btn-pop px-4 py-2.5 bg-gray-100 text-gray-600 rounded-xl text-sm font-medium hover:bg-gray-200"
              >
                Clear
              </button>
            )}
          </div>
        </div>

        {/* Info note */}
        <div className="mt-5 p-3 bg-blue-50/80 rounded-xl">
          <p className="text-xs text-blue-600">
            <strong className="font-semibold">Priority:</strong> Browser GPS is used first if available. Your home location is the fallback when GPS is off or denied.
          </p>
        </div>

        {/* Divider */}
        <div className="border-t border-gray-200/60 mt-5 mb-5"></div>

        {/* Notifications & Prompts */}
        <div className="mb-2">
          <p className="font-condensed text-sm font-semibold text-gray-600 uppercase tracking-wide mb-3">Notifications</p>
          <label className="flex items-center justify-between cursor-pointer group">
            <div className="flex items-center gap-3">
              <span className="text-lg">🙏</span>
              <div>
                <p className="text-sm font-medium text-gray-700">Kudos prompt after claiming</p>
                <p className="text-xs text-gray-400">Ask to send thanks to the donor</p>
              </div>
            </div>
            <button
              onClick={() => onToggleKudosPrompt && onToggleKudosPrompt(!kudosPromptEnabled)}
              className={`relative w-11 h-6 rounded-full transition-colors duration-200 ${kudosPromptEnabled ? "bg-green-500" : "bg-gray-300"}`}
            >
              <span
                className={`absolute top-0.5 left-0.5 w-5 h-5 bg-white rounded-full shadow transition-transform duration-200 ${kudosPromptEnabled ? "translate-x-5" : "translate-x-0"}`}
              />
            </button>
          </label>
        </div>

        {/* Divider */}
        <div className="border-t border-gray-200/60 mt-5 mb-5"></div>

        {/* Account section */}
        <div>
          <p className="font-condensed text-sm font-semibold text-gray-600 uppercase tracking-wide mb-3">Account</p>
          {userEmail && (
            <p className="text-xs text-gray-500 mb-3 truncate">Signed in as <span className="font-medium text-gray-700">{userEmail}</span></p>
          )}
          <button
            onClick={onLogout}
            className="btn-pop w-full py-2.5 bg-red-50 text-red-600 rounded-xl text-sm font-semibold hover:bg-red-100 border border-red-200/60"
          >
            Log Out
          </button>
        </div>
      </div>
    </div>
  );
}
